import React, { useState } from 'react';
import { useTranslation } from '../contexts/TranslationContext';

const Contact = () => {
    const { t } = useTranslation();
    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });

    const handleContactSubmit = (e) => {
        e.preventDefault();
        const { name, email, phone, subject, message } = contactForm;

        const mailSubject = subject || `${t('contact.title')} - ${name}`;

        const body = `${t('contact.emailName')}: ${name}
${phone ? `${t('contact.emailPhone')}: ${phone}` : ''}
${t('contact.emailEmail')}: ${email}

${t('contact.emailMessage')}:
${message}`;

        const mailtoLink = `mailto:${process.env.REACT_APP_COMPANY_EMAIL}?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(body)}`;
        window.location.href = mailtoLink;
    };

    const handleInputChange = (e) => {
        setContactForm({
            ...contactForm,
            [e.target.name]: e.target.value
        });
    };

    return (
        <div className="bg-white pt-16 sm:pt-20">
            <section className="section-padding bg-neutral-50">
                <div className="container-custom">
                    {/* Mobile: Stack vertically, Desktop: Side by side */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-16">
                        {/* Contact Information */}
                        <div className="bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg border border-gray-100 p-5 sm:p-8 md:p-10 space-y-6 sm:space-y-10 order-2 lg:order-1">
                            <div>
                                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-950 mb-4 sm:mb-6">
                                    {t('contact.contactInfo')}
                                </h2>
                                <p className="text-base sm:text-lg text-neutral-700 leading-relaxed">
                                    {t('contact.contactDescription')}
                                </p>
                            </div>

                            {/* Contact Details - Mobile optimized cards */}
                            <div className="space-y-4 sm:space-y-8">
                                {/* Address */}
                                <a 
                                    href="https://maps.google.com/?q=Sigmundweg+2,+39030+Kiens"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-start space-x-3 sm:space-x-4 p-4 sm:p-6 bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg border border-gray-100 touch-feedback active:scale-[0.99] transition-transform"
                                >
                                    <div className="w-11 h-11 sm:w-14 sm:h-14 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                                        <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-semibold text-neutral-950 mb-1">{t('contact.address')}</h3>
                                        <p className="text-sm sm:text-base text-neutral-700">{t('contact.addressText')}</p>
                                    </div>
                                </a>

                                {/* Phone */}
                                <a 
                                    href="tel:+390474565678"
                                    className="flex items-start space-x-3 sm:space-x-4 p-4 sm:p-6 bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg border border-gray-100 touch-feedback active:scale-[0.99] transition-transform"
                                >
                                    <div className="w-11 h-11 sm:w-14 sm:h-14 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                                        <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-semibold text-neutral-950 mb-1">{t('common.phone')}</h3>
                                        <p className="text-sm sm:text-base text-neutral-700">{t('contact.phoneText')}</p>
                                    </div>
                                </a>

                                {/* Email */}
                                <div className="flex items-start space-x-3 sm:space-x-4 p-4 sm:p-6 bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg border border-gray-100">
                                    <div className="w-11 h-11 sm:w-14 sm:h-14 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                                        <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                            <a href="mailto:alex@buergstaller.it" className="block touch-feedback">
                                                <h3 className="text-base sm:text-xl font-semibold text-neutral-950 mb-1">{t('contact.regularEmail')}</h3>
                                                <p className="text-sm sm:text-base text-neutral-700 truncate">{t('contact.regularEmailText')}</p>
                                            </a>
                                            <a href="mailto:alexbuergstaller@pec.it" className="block touch-feedback">
                                                <h3 className="text-base sm:text-xl font-semibold text-neutral-950 mb-1">{t('contact.pecEmail')}</h3>
                                                <p className="text-sm sm:text-base text-neutral-700 truncate">{t('contact.pecEmailText')}</p>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Business Information - Mobile optimized */}
                            <div className="p-5 sm:p-8 rounded-xl sm:rounded-2xl border border-gray-100 bg-gray-50">
                                <h3 className="text-xl sm:text-2xl font-semibold text-neutral-950 mb-4 sm:mb-6">{t('contact.businessInfo')}</h3>
                                <div className="space-y-3 sm:space-y-4">
                                    <div className="flex items-start">
                                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-primary-500 rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0 mt-0.5 shadow-sm">
                                            <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <span className="text-sm sm:text-lg text-neutral-700 break-all">{t('contact.vatNumber')}</span>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-primary-500 rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0 mt-0.5 shadow-sm">
                                            <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                        </div>
                                        <span className="text-sm sm:text-lg text-neutral-700">{t('contact.recipientCode')}</span>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-primary-500 rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0 mt-0.5 shadow-sm">
                                            <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                        </div>
                                        <span className="text-sm sm:text-lg text-neutral-700">{t('contact.registryNumber')}</span>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-primary-500 rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0 mt-0.5 shadow-sm">
                                            <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                            </svg>
                                        </div>
                                        <span className="text-sm sm:text-lg text-neutral-700 break-all">{t('contact.ibanPlaceholder')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form - Mobile first, shown first on mobile */}
                        <div className="bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg border border-gray-100 p-5 sm:p-8 md:p-10 order-1 lg:order-2">
                            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-950 mb-6 sm:mb-8">
                                {t('contact.sendMessage')}
                            </h2>
                            <form onSubmit={handleContactSubmit} className="space-y-4 sm:space-y-6">
                                {/* Name and Email - Stack on mobile */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                    <div>
                                        <label htmlFor="name" className="block text-sm sm:text-base font-semibold text-neutral-700 mb-2">
                                            {t('common.name')} {t('contact.required')}
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            required
                                            value={contactForm.name}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 sm:px-5 sm:py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-base touch-target"
                                            placeholder={t('contact.yourName')}
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="email" className="block text-sm sm:text-base font-semibold text-neutral-700 mb-2">
                                            {t('common.email')} {t('contact.required')}
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            required
                                            value={contactForm.email}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 sm:px-5 sm:py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-base touch-target"
                                            placeholder={t('contact.yourEmail')}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="phone" className="block text-sm sm:text-base font-semibold text-neutral-700 mb-2">
                                        {t('contact.phoneOptional')}
                                    </label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={contactForm.phone}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 sm:px-5 sm:py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-base touch-target"
                                        placeholder={t('contact.phonePlaceholder')}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-sm sm:text-base font-semibold text-neutral-700 mb-2">
                                        {t('common.message')} {t('contact.required')}
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        required
                                        rows={6}
                                        value={contactForm.message}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 sm:px-5 sm:py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none text-base"
                                        placeholder={t('contact.messagePlaceholder')}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="btn-primary w-full px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold transform hover:scale-105 active:scale-95 shadow-primary-glow touch-target"
                                >
                                    {t('contact.sendMessage')}
                                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;